/**
 * Group Management Component
 *
 * Manage user groups for better organization
 * Features:
 * - Create/Edit/Delete groups
 * - Assign users to groups
 * - Department-based grouping
 * - View group members
 */

import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Avatar,
  List,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { get_departments } from '@/utils/api';
import { accessControlUtils } from '@/components/AccessControlUtils';
import DataTable from '@/components/DataTable';
import { EmptyState } from '@/components/PageHeader';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import PageModal from '@/components/PageModal';
import HeaderActions from '@/components/HeaderActions';

const { Option } = Select;
const { TextArea } = Input;

const GroupManagement = ({ active = true }) => {
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [viewMembersModal, setViewMembersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [form] = Form.useForm();

  const access = accessControlUtils("User Management");

  useEffect(() => {
    loadGroups();
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      await get_departments((res) => {
        if (res.data && res.data.data) {
          setDepartments(res.data.data);
        }
      });
    } catch (error) {
      console.error("Failed to load departments:", error);
      toast.error(error?.response?.data?.message || "Failed to load departments");
    }
  };

  const loadGroups = async () => {
    setLoading(true);
    try {
      // Replace with your actual API call
      // For now using mock data
      const mockGroups = [
        {
          group_id: 1,
          group_name: "Production Team A",
          description: "Morning shift production team",
          department_id: 1,
          department_name: "Production",
          member_count: 12,
          is_active: true,
        },
        {
          group_id: 2,
          group_name: "QC Lab Team",
          description: "Quality control laboratory team",
          department_id: 2,
          department_name: "Quality Control",
          member_count: 8,
          is_active: true,
        },
      ];
      setGroups(mockGroups);
    } catch (error) {
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingGroup(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    form.setFieldsValue({
      group_name: group.group_name,
      description: group.description,
      department_id: group.department_id,
      is_active: group.is_active !== false,
    });
    setModalVisible(true);
  };

  const handleDelete = async (groupId) => {
    try {
      // Replace with your actual delete API
      toast.success("Group deleted successfully");
      loadGroups();
    } catch (error) {
      toast.error("Failed to delete group");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Replace with your actual API call
      const groupData = {
        group_name: values.group_name,
        description: values.description,
        department_id: values.department_id,
        is_active: values.is_active !== false,
        ...(editingGroup && { id: editingGroup.group_id }),
      };

      toast.success(editingGroup ? "Group updated successfully" : "Group created successfully");
      setModalVisible(false);
      form.resetFields();
      loadGroups();
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const handleViewMembers = (group) => {
    setSelectedGroup(group);
    setViewMembersModal(true);
  };



  const columns = [
    {
      title: "Group Name",
      dataIndex: "group_name",
      key: "group_name",
      width: 300,
      render: (text, record) => (
        <Space>
          <TeamOutlined style={{ color: defaultColors.primary, fontSize: 18 }} />
          <strong>{text}</strong>
          {!record.is_active && <Tag color="red">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      width: 220,
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 320,
      render: (text) => text || <span style={{ color: "#999" }}>No description</span>,
    },
    {
      title: "Members",
      key: "members",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Tooltip title="View group members">
          <Button
            size="small"
            aria-label={`View ${record.member_count || 0} members of ${record.group_name}`}
            icon={<UserOutlined style={{ color: defaultColors.primary }} />}
            onClick={() => handleViewMembers(record)}
          >
            {record.member_count || 0} {(record.member_count || 0) === 1 ? "member" : "members"}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      align: "center",
      render: (_, record) => (
        <Space>
          {access?.update_access && (
            <Button
              size="small"
              aria-label={`Edit ${record.group_name}`}
              icon={<Icons.Pencil className="w-3.5 h-3.5 text-amber-600" />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          {access?.delete_access && (
            <Popconfirm
              title="Delete this group?"
              description="Users in this group will be unassigned."
              onConfirm={() => handleDelete(record.group_id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{
                className: "bg-red-600 text-white hover:bg-red-700",
              }}
            >
              <Button size="small" danger aria-label={`Delete ${record.group_name}`} icon={<Icons.Trash2 className="w-3.5 h-3.5" />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Mock members data
  const mockMembers = [
    { id: 1, name: "John Doe", role: "Production Operator", avatar: null },
    { id: 2, name: "Jane Smith", role: "Supervisor", avatar: null },
  ];

  return (
    <>
      {/* hosted in User Management: Add Group goes in that page's header, only while this tab is open */}
      {active && access?.create_access && (
        <HeaderActions>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Group
          </Button>
        </HeaderActions>
      )}
      <DataTable
        tableKey="groupManagementTable"
        searchable
        searchKeys={['group_name', 'description', 'department_name']}
        searchPlaceholder="Search groups…"
        columnConfig
        showCount={false}
        exportable
        exportFilename="groups"
        bordered
        className="group-management-table"
        data={groups}
        columns={columns}
        rowKey="group_id"
        loading={loading}
        scrollX="max-content"
        height={null}
        locale={{
          emptyText: (
            <EmptyState
              title="No groups yet"
              description="Create a group to organise users by team or department."
              actionLabel={access?.create_access ? 'Add Group' : undefined}
              onAction={handleAdd}
            />
          ),
        }}
      />

      {/* Add/Edit Modal */}
      <PageModal
        open={modalVisible}
        icon={<TeamOutlined style={{ fontSize: 22 }} />}
        title={editingGroup ? "Edit Group" : "Create New Group"}
        description="A group organises users by team or department."
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" onClick={handleSubmit}>
              {editingGroup ? "Update" : "Create"}
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="group_name"
            label="Group Name"
            rules={[
              { required: true, message: "Please enter group name" },
              { min: 3, message: "Group name must be at least 3 characters" },
            ]}
          >
            <Input placeholder="e.g., Production Team A, QC Lab Team" />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: "Please select department" }]}
          >
            <Select placeholder="Select department">
              {departments.map((dept) => (
                <Option key={dept.department_id} value={dept.department_id}>
                  {dept.department_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Describe the purpose and responsibilities of this group..."
            />
          </Form.Item>
        </Form>
      </PageModal>

      {/* View Members Modal */}
      <PageModal
        open={viewMembersModal}
        icon={<UserOutlined style={{ fontSize: 22 }} />}
        title="Group Members"
        description={selectedGroup?.group_name}
        onCancel={() => setViewMembersModal(false)}
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setViewMembersModal(false)}>Close</Button>
          </div>
        }
        width={500}
      >
        <List
          itemLayout="horizontal"
          dataSource={mockMembers}
          renderItem={(member) => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={member.name}
                description={member.role}
              />
            </List.Item>
          )}
        />
      </PageModal>
    </>
  );
};

export default GroupManagement;
